import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  DesignTestInputDto,
  DesignTestOutputDto
} from '../../../models/job-request-iterations-dto.interface';

interface RheologyReportRow {
  temperature?: number;
  rpm600?: number;
  rpm300?: number;
  rpm200?: number;
  rpm100?: number;
  rpm6?: number;
  rpm3?: number;
  plasticViscosity?: number;
  yieldPoint?: number;
  gel10Sec?: number;
  gel10Min?: number;
  gel30Min?: number;
}

@Component({
  selector: 'app-rheology-test-report',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './rheology-test-report.component.html',
  styleUrl: './rheology-test-report.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RheologyTestReportComponent {
  private static readonly DEFAULT_RHEOLOGY_TEMPERATURES = [80, 120, 150];

  @Input({ required: true }) test!: DesignTestInputDto;
  @Input({ required: true }) output!: DesignTestOutputDto;

  protected rheologyReportRows(): RheologyReportRow[] | null {
    const outputReadings = this.rheologyXmlReadings(this.output.content, 'RheologyOutputReading');
    if (outputReadings.length === 0) return null;

    const inputReadings = this.rheologyXmlReadings(this.test.content, 'RheologyInputReading');
    const inputById = new Map<string, Element>();
    for (const reading of inputReadings) {
      const id = this.rheologyXmlValue(reading, 'Id');
      if (id) inputById.set(id, reading);
    }

    const rows = outputReadings
      .map((reading, index) => {
        const readingId = this.rheologyXmlValue(reading, 'Id');
        const inputReading = (readingId ? inputById.get(readingId) : undefined) ?? inputReadings[index];
        const temperature = this.toNumber(
          this.rheologyXmlValue(inputReading, 'Temperature')
            ?? this.rheologyXmlValue(reading, 'Temperature')
            ?? this.parsedInputTemperature(index)
            ?? RheologyTestReportComponent.DEFAULT_RHEOLOGY_TEMPERATURES[index]
        );
        const rpm600 = this.rheologyNumber(reading, 'Viscosity600Rpm');
        const rpm300 = this.rheologyNumber(reading, 'Viscosity300Rpm');
        const plasticViscosity = rpm600 !== undefined && rpm300 !== undefined
          ? rpm600 - rpm300
          : undefined;

        return {
          temperature,
          rpm600,
          rpm300,
          rpm200: this.rheologyNumber(reading, 'Viscosity200Rpm'),
          rpm100: this.rheologyNumber(reading, 'Viscosity100Rpm'),
          rpm6: this.rheologyNumber(reading, 'Viscosity6Rpm'),
          rpm3: this.rheologyNumber(reading, 'Viscosity3Rpm'),
          plasticViscosity,
          yieldPoint: plasticViscosity !== undefined && rpm300 !== undefined
            ? rpm300 - plasticViscosity
            : undefined,
          gel10Sec: this.rheologyNumber(reading, 'Gel10Sec'),
          gel10Min: this.rheologyNumber(reading, 'Gel10Min'),
          gel30Min: this.rheologyNumber(reading, 'Gel30Min')
        };
      })
      .filter(row => row.temperature !== undefined);

    return rows.length ? rows : null;
  }

  private rheologyXmlReadings(content: string | null, readingName: string): Element[] {
    if (!content) return [];

    const parsed = new DOMParser().parseFromString(content, 'application/xml');
    if (parsed.querySelector('parsererror')) return [];

    const acceptedNames = new Set([
      readingName.toLowerCase(),
      `${readingName}legacy`.toLowerCase(),
      'reading'
    ]);
    return Array.from(parsed.getElementsByTagName('*'))
      .filter(element => acceptedNames.has(element.localName.toLowerCase()));
  }

  private rheologyXmlValue(element: Element | undefined, name: string): string | null {
    if (!element) return null;

    const child = Array.from(element.children)
      .find(candidate => candidate.localName.toLowerCase() === name.toLowerCase());
    const value = child?.textContent?.trim();
    return value || null;
  }

  private rheologyNumber(element: Element, name: string): number | undefined {
    return this.toNumber(this.rheologyXmlValue(element, name));
  }

  private parsedInputTemperature(index: number): string | null {
    const temperatures = this.test.parsedInputValues
      .filter(field => field.label.replace(/[\s_-]+/g, '').toLowerCase() === 'temperature')
      .map(field => field.value);
    return temperatures[index] ?? null;
  }

  private toNumber(value: string | number | null | undefined): number | undefined {
    if (value === null || value === undefined || value === '') return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
}